import * as React from 'react';
import { Outlet, createFileRoute, redirect, useNavigate, useRouterState } from '@tanstack/react-router';
import { Spinner } from '@finmin/ui';
import { NavBar } from '@/components/nav-bar';
import { NotificationsProvider } from '@/features/notifications/notifications-provider';
import { UnmaskProvider } from '@/features/unmask';
import { PanViewProvider } from '@/features/dashboard/pan-view-context';
import { tokenStore } from '@/lib/api';
import { useProfile } from '@/api/profile';
import { ChatWidget } from '@/features/chat-widget/chat-widget';

/**
 * Layout route that gates everything beneath it on auth status.
 * Also redirects to /questionnaire when the profile is not yet complete.
 * Renders the global NavBar above every protected page.
 */
export const Route = createFileRoute('/_protected')({
  beforeLoad: ({ context, location }) => {
    if (context.auth.status === 'authenticated') return;
    if (context.auth.status === 'loading') return; // wait for auth to resolve

    // `context.auth` is React state piped into the router context, so it can
    // lag by a tick right after login/signup (the state flips to
    // `authenticated`, but the guard still sees the previous value and would
    // wrongly bounce a just-authenticated user back to /login).
    //
    // The token store is the SYNCHRONOUS source of truth: the refresh token is
    // written to localStorage during verify-success, before any navigation. If
    // it's present, let the app render — AuthProvider then settles the state,
    // and a genuinely-invalid session is still logged out by the api-client's
    // 401 → auth-failure handler.
    if (tokenStore.getRefreshToken()) return;

    throw redirect({
      to: '/login',
      search: { redirect: location.href },
    });
  },
  component: ProtectedLayout,
});

function ProtectedLayout() {
  const { data: profile, isSuccess } = useProfile();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Once we know the profile is incomplete, send the user to the questionnaire.
  // The /questionnaire route itself is exempt from this redirect so we don't loop.
  const incomplete = Boolean(profile) && !profile?.isProfileComplete;
  React.useEffect(() => {
    if (isSuccess && incomplete && pathname !== '/questionnaire') {
      void navigate({ to: '/questionnaire', replace: true });
    }
  }, [isSuccess, incomplete, pathname, navigate]);

  return (
    <NotificationsProvider>
      <UnmaskProvider>
        <PanViewProvider>
          <div className="min-h-screen bg-background">
            <NavBar />
            <Outlet />
            <ChatWidget />
          </div>
        </PanViewProvider>
      </UnmaskProvider>
    </NotificationsProvider>
  );
}

export function AuthLoading() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Spinner className="h-6 w-6" />
    </div>
  );
}
